import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../Button/Button';
import { useLanguage } from '../../contexts/LanguageContext';
import type { StaffReassignmentConflictDetails } from '../../hooks/useStaff';
import styles from './ConfirmBookingConflictDialog.module.css';

/**
 * AB#1862 — Force-with-confirm dialog shown when the BE rejects a
 * `SetStaffBranches` / `SetStaffServices` save with
 * `STAFF_REASSIGN_HAS_ACTIVE_BOOKINGS`. Surfaces the count + earliest
 * appointment + affected entities, plus a deep-link to the owner Calendar
 * filtered by branch (the existing `/calendar?branchId=N` deep-link is the
 * only param the page reads today — `staffId` is passed forward-compatibly
 * for a follow-up).
 *
 * Three actions:
 *   - Cancel       → close + keep the modal state as it was.
 *   - View calendar → react-router push to /calendar?branchId=...
 *   - Continue anyway → caller retries the original mutation with
 *                       `forceWithActiveBookings: true`.
 *
 * Per Faris's arch-brief §6.2 + the wiki page at
 * `/Business Portal/Manage Staff/Booking conflict guard`. Sibling component
 * `BookingConflictBlockBanner` is the read-only variant used by the Delete
 * path (no force CTA).
 */
export interface ConfirmBookingConflictDialogProps {
    isOpen: boolean;
    /** AB#1862 — structured payload from the BE 422. */
    details: StaffReassignmentConflictDetails;
    /** Owner-friendly label of the staff being mutated (e.g. "Lina Smith"). */
    staffName: string;
    /** Map of branchId → branch display name, used for the affected-list. */
    branchNames?: Map<number, string>;
    /** Map of serviceId → service display name, used for the affected-list. */
    serviceNames?: Map<number, string>;
    /**
     * Inline error to surface inside the dialog (e.g. the force-retry itself
     * failed with a network error). Falsy → no inline error. The dialog
     * stays open so the owner can re-try without losing the context.
     */
    inlineError?: string | null;
    /** Disable both action buttons while the force-retry is in flight. */
    busy?: boolean;
    onCancel: () => void;
    /**
     * Owner pressed "Continue anyway" — the caller retries the original
     * mutation with `forceWithActiveBookings: true`. On success, the caller
     * is responsible for closing the dialog.
     */
    onConfirmForce: () => void;
}

/**
 * Locale-aware short date+time formatter. Picks the language off the active
 * LanguageContext (en → en-US, ar → ar-EG fallback). Falls back to the ISO
 * string if Intl is unavailable.
 */
function formatEarliestDate(iso: string, language: 'en' | 'ar'): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    try {
        const locale = language === 'ar' ? 'ar-EG' : 'en-US';
        return new Intl.DateTimeFormat(locale, {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(date);
    } catch {
        return iso;
    }
}

/**
 * Render `{count}` / `{earliest}` placeholders in the localized body copy.
 * The `t()` function is interpolation-free (see LanguageContext.tsx
 * line 837) so substitution happens here at call-site, matching the
 * existing `business.serviceType.flip.*` precedent.
 */
function interpolate(template: string, vars: Record<string, string | number>): string {
    return Object.keys(vars).reduce(
        (acc, key) => acc.split(`{${key}}`).join(String(vars[key])),
        template,
    );
}

const ConfirmBookingConflictDialog: React.FC<ConfirmBookingConflictDialogProps> = ({
    isOpen,
    details,
    staffName,
    branchNames,
    serviceNames,
    inlineError,
    busy,
    onCancel,
    onConfirmForce,
}) => {
    const { t, language } = useLanguage();
    const navigate = useNavigate();

    if (!isOpen) return null;

    const isServicesHandler = details.handler === 'SetStaffServices';
    const earliestPretty = formatEarliestDate(details.earliestAppointmentDate, language);

    // Body template — branch vs. service handlers get their own copy
    // (different noun in the sentence) so the localized string reads
    // naturally in both EN and AR. SetStaffBranches uses the default key,
    // SetStaffServices uses the *.services variant.
    const bodyTemplate = isServicesHandler
        ? t('staff.reassign.conflict.body.services')
        : t('staff.reassign.conflict.body');
    const bodyCopy = interpolate(bodyTemplate, {
        count: details.count,
        earliest: earliestPretty,
    });

    // Affected entity names — render an `<ul>` of the branch / service names
    // when we have them, so the owner can verify before forcing. Falls back
    // silently if the parent didn't pass the maps (the count + body already
    // carries the essential info).
    const affectedNames: string[] = isServicesHandler
        ? details.affectedServiceIds.map(id => serviceNames?.get(id) ?? `#${id}`)
        : details.affectedBranchIds.map(id => branchNames?.get(id) ?? `#${id}`);

    /**
     * Deep-link to the owner Calendar filtered by the first affected
     * branch. CalendarPage today reads `branchId`, `day`, `apptId` from the
     * query string (CalendarPage.tsx line 106). `staffId` and `serviceId`
     * are forwarded as forward-compatible hints — the page silently
     * ignores them today and a follow-up PBI can wire `staffId` into the
     * grid filter without a contract change.
     */
    const handleViewCalendar = () => {
        const params = new URLSearchParams();
        const earliestDay = details.earliestAppointmentDate.slice(0, 10); // YYYY-MM-DD
        if (earliestDay) params.set('day', earliestDay);
        if (!isServicesHandler && details.affectedBranchIds[0] != null) {
            params.set('branchId', String(details.affectedBranchIds[0]));
        }
        if (isServicesHandler && details.affectedServiceIds[0] != null) {
            params.set('serviceId', String(details.affectedServiceIds[0]));
        }
        // staffId is always known — pass it forward-compatibly even though
        // CalendarPage doesn't yet read it. Avoids a follow-up FE change
        // when the page picks it up.
        navigate(`/calendar?${params.toString()}`);
    };

    return (
        <div
            className={`${styles.modalOverlay} ${styles.open}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="bc-confirm-title"
            aria-describedby="bc-confirm-body"
        >
            <div className={`${styles.modalContent} ${styles.open}`}>
                {/* The close-X is a visual affordance only — the actual
                    Cancel intent is fired by the labelled button below.
                    Its aria-label uses a generic `Close` semantic so the
                    labelled Cancel button stays the canonical accessible-
                    name match in screen readers AND in tests. */}
                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={onCancel}
                    aria-label="Close"
                    disabled={busy}
                    data-testid="bc-confirm-close-x"
                >
                    &times;
                </button>
                <div className={styles.iconWrap} aria-hidden="true">!</div>
                <h2 id="bc-confirm-title" className={styles.header}>
                    {t('staff.reassign.conflict.title')}
                </h2>
                <p id="bc-confirm-body" className={styles.body}>
                    {/* Lead with staff name so the owner has context if multiple
                        modal interactions stacked in their working memory. */}
                    <strong>{staffName}</strong>: {bodyCopy}
                </p>
                {affectedNames.length > 0 && (
                    <ul className={styles.affectedList} aria-label="Affected items">
                        {affectedNames.map((name, idx) => (
                            <li key={`${name}-${idx}`}>{name}</li>
                        ))}
                    </ul>
                )}
                <button
                    type="button"
                    className={styles.calendarLink}
                    onClick={handleViewCalendar}
                >
                    {t('staff.reassign.conflict.viewCalendar')} →
                </button>
                {inlineError && (
                    <p className={styles.inlineError} role="alert">
                        {inlineError}
                    </p>
                )}
                <div className={styles.buttonsContainer}>
                    <Button
                        label={t('staff.reassign.conflict.cancel')}
                        onClick={onCancel}
                        noAppearance={true}
                        size="small"
                        disabled={busy}
                    />
                    <div>
                        {/* Amber tint signals "destructive-but-allowed". The
                            label includes the count so the owner can't
                            mis-click a 1-booking case as a 50-booking case. */}
                        <Button
                            label={interpolate(t('staff.reassign.conflict.forceProceed'), {
                                count: details.count,
                            })}
                            onClick={onConfirmForce}
                            size="medium"
                            backgroundColor="var(--warning-fill)"
                            fontColor="var(--on-warning)"
                            disabled={busy}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmBookingConflictDialog;
